import * as Tone from 'tone';
import { Voice } from './voice.js';
import { EffectsChain } from './effects.js';
import { Arpeggiator } from './arpeggiator.js';
import { LFOModule } from './lfo.js';

const NUM_VOICES = 8;

export class AudioEngine {
  constructor(state) {
    this.state = state;
    this.started = false;

    // Signal chain nodes (created but not connected until start)
    this.filter = new Tone.Filter({ frequency: 5000, type: 'lowpass', Q: 1 });
    this.effects = new EffectsChain();
    this.masterGain = new Tone.Gain(0.7);

    // Analyzers
    this.waveformAnalyzer = new Tone.Analyser('waveform', 256);
    this.fftAnalyzer = new Tone.Analyser('fft', 256);

    // Create voices
    this.voices = [];
    for (let i = 0; i < NUM_VOICES; i++) {
      const voice = new Voice(this.filter);
      this.voices.push(voice);
    }

    // Wire: filter → effects → master → analyzers → destination
    this.effects.connectInput(this.filter);
    this.effects.connectOutput(this.masterGain);
    this.masterGain.connect(this.waveformAnalyzer);
    this.masterGain.connect(this.fftAnalyzer);
    this.masterGain.toDestination();

    // Arpeggiator
    this.arpeggiator = new Arpeggiator(
      (note, vel) => this._voiceNoteOn(note, vel),
      (note) => this._voiceNoteOff(note)
    );

    // LFO
    this.lfoModule = new LFOModule(this);

    // Note-on callback for visuals
    this._onNoteOnCallback = null;

    // Subscribe to state changes
    this._subscribeAll();
  }

  async start() {
    if (this.started) return;
    await Tone.start();
    this.started = true;
  }

  onNoteOn(callback) {
    this._onNoteOnCallback = callback;
  }

  noteOn(note, velocity = 0.8) {
    if (this.arpeggiator.enabled) {
      this.arpeggiator.addNote(note);
    } else {
      this._voiceNoteOn(note, velocity);
    }
  }

  noteOff(note) {
    if (this.arpeggiator.enabled) {
      this.arpeggiator.removeNote(note);
    } else {
      this._voiceNoteOff(note);
    }
  }

  _voiceNoteOn(note, velocity) {
    // Find a free voice or steal the oldest
    let voice = this.voices.find((v) => !v.active);
    if (!voice) {
      // Voice stealing: release the first active voice
      voice = this.voices[0];
      voice.noteOff();
    }
    voice.noteOn(note, velocity);

    if (this._onNoteOnCallback) {
      this._onNoteOnCallback(note, velocity);
    }
  }

  _voiceNoteOff(note) {
    for (const voice of this.voices) {
      if (voice.active && voice.note === note) {
        voice.noteOff();
        break;
      }
    }
  }

  getWaveformData() {
    return this.waveformAnalyzer.getValue();
  }

  getFFTData() {
    return this.fftAnalyzer.getValue();
  }

  _subscribeAll() {
    const s = this.state;

    s.subscribe('osc1.type', (val) => {
      this.voices.forEach((v) => v.setOsc1Type(val));
    });

    s.subscribe('osc2.type', (val) => {
      this.voices.forEach((v) => v.setOsc2Type(val));
    });

    s.subscribe('osc2.detune', (val) => {
      this.voices.forEach((v) => v.setDetune(val));
    });

    s.subscribe('oscMix', (val) => {
      this.voices.forEach((v) => v.setMix(val));
    });

    s.subscribe('fmEnabled', (val) => {
      const amount = s.get('fmAmount');
      this.voices.forEach((v) => v.setFM(val, amount));
    });

    s.subscribe('fmAmount', (val) => {
      const enabled = s.get('fmEnabled');
      this.voices.forEach((v) => v.setFM(enabled, val));
    });

    s.subscribe('filter.cutoff', (val) => {
      this.filter.frequency.setValueAtTime(val, Tone.now());
    });

    s.subscribe('filter.resonance', (val) => {
      this.filter.Q.setValueAtTime(val, Tone.now());
    });

    s.subscribe('filter.type', (val) => {
      this.filter.type = val;
    });

    s.subscribe('ampEnv', (val) => {
      this.voices.forEach((v) => v.setAmpEnv(val));
    });

    // Effects
    s.subscribe('effects.reverb', (val) => this.effects.setReverb(val));
    s.subscribe('effects.delay', (val) => this.effects.setDelay(val));
    s.subscribe('effects.chorus', (val) => this.effects.setChorus(val));
    s.subscribe('effects.distortion', (val) => this.effects.setDistortion(val));
    s.subscribe('effects.phaser', (val) => this.effects.setPhaser(val));

    // Arpeggiator
    s.subscribe('arp.enabled', (val) => this.arpeggiator.setEnabled(val));
    s.subscribe('arp.pattern', (val) => this.arpeggiator.setPattern(val));
    s.subscribe('arp.rate', (val) => this.arpeggiator.setRate(val));
    s.subscribe('arp.octaves', (val) => this.arpeggiator.setOctaves(val));

    // LFO
    s.subscribe('lfo.rate', (val) => this.lfoModule.setRate(val));
    s.subscribe('lfo.depth', (val) => this.lfoModule.setDepth(val));
    s.subscribe('lfo.destination', (val) => this.lfoModule.setDestination(val));

    // Master
    s.subscribe('master.volume', (val) => {
      this.masterGain.gain.setValueAtTime(val, Tone.now());
    });
  }
}
